// src/features/Management/page/Users/index.tsx
import { Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import DynamicTable from '@/common/components/DynamicTable/DynamicTable';
import { API_ENDPOINTS, QUERY_KEYS } from '../../api/apiEndpoints';
import type { UserType } from '../../types/managementTypes';
import Confirmation from '@/common/components/ConfirmButton';
import { useTranslation } from 'react-i18next';
import { CheckCircleOutlined, StopOutlined, SyncOutlined } from '@ant-design/icons';

const UserPage = () => {
    const { t } = useTranslation();

    const myActions = [
        {
            render: (record: UserType) => {
                if (!record.isActive && !record.isEmailActivated) {
                    return (
                        <Confirmation
                            apiUrl={API_ENDPOINTS.MANAGEMENT.RESEND_ACTIVATION_EMAIL_USER}
                            id={record.email}
                            title={t('Resend Email')}
                            method="post"
                            icon={<SyncOutlined />}
                            payloadKey="email" // Capitalized as per your API
                            confirmButtonText={t(`Are you sure you want to resend activation email ?`)}
                            queryKey={QUERY_KEYS.MANAGEMENT.USERS}
                            successMessage={t("Email sent successfully")}
                        />
                    );
                }
                else if (record.isEmailActivated) {
                    return record.isActive ? (
                        // Subcase: Active → show Deactivate
                        <Confirmation
                            apiUrl={API_ENDPOINTS.MANAGEMENT.ACTIVATE_DEACTIVATE_USER}
                            id={record.id}
                            title={t("De-Activate")}
                            method="post"
                            icon={<StopOutlined style={{ color: "red" }} />}
                            isPostById={true}
                            payloadKey="Id"
                            confirmButtonText={t("Are you sure you want to deactivate selected user?")}
                            queryKey={QUERY_KEYS.MANAGEMENT.USERS}
                            successMessage={t("User deactivated successfully")}
                        />
                    ) : (
                        // Subcase: Inactive → show Activate
                        <Confirmation
                            apiUrl={API_ENDPOINTS.MANAGEMENT.ACTIVATE_DEACTIVATE_USER}
                            id={record.id}
                            title={t("Activate")}
                            method="post"
                            icon={<CheckCircleOutlined style={{ color: "green" }} />}
                            isPostById={true}
                            payloadKey="Id"
                            confirmButtonText={t("Are you sure you want to activate selected user?")}
                            queryKey={QUERY_KEYS.MANAGEMENT.USERS}
                            successMessage={t("User activated successfully")}
                        />
                    );
                }
                return <span></span>;
            },
        },
    ];

    // User Table Columns
    const userColumns: ColumnsType<UserType> = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 150
        },
        {
            title: 'User Name',
            dataIndex: 'userName',
            key: 'userName',
            width: 170
        },
        {
            title: 'Phone No',
            dataIndex: 'cellNo',
            key: 'cellNo',
            width: 120
        },
        {
            title: t('User Type'),
            dataIndex: 'userType',
            key: 'userType',
            width: 120
        },
        {
            title: 'Active',
            dataIndex: 'isActive',
            key: 'isActive',
            width: 100,
            render: (isActive: boolean) => (
                <Tag color={isActive ? 'green' : 'red'}>
                    {isActive ? 'Yes' : 'No'}
                </Tag>
            ),
        },
    ];



    return (
        <>
            <DynamicTable<UserType>
                tableTitle={'User Management'}
                dataApiUrl={API_ENDPOINTS.MANAGEMENT.GET_USERS}
                baseQueryKey={QUERY_KEYS.MANAGEMENT.USERS}
                columns={userColumns}
                showActions={true}
                customActions={myActions}
                filterConfigs={[
                    { field: 'name', placeholder: 'Search by Name' },
                    { field: 'userName', placeholder: 'Search by Username' },
                ]}
            />
        </>
    );
};

export default UserPage;
